import {
  EVENT_SIMULATION_NOTE,
  RECLASSIFIABLE_SEVERITIES,
  type ReclassifiableSeverity,
  SIMULATED_EVENT_KINDS,
  SIMULATION_AUDIENCES,
  type SimulatedEventKind,
  type SimulationAudience,
} from '@fos/shared/contracts/inbox';
import { SEVERITY_LABELS, SIMULATED_EVENT_KIND_LABELS, SIMULATION_AUDIENCE_LABELS } from '@fos/shared/labels';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { toast } from '../../../ui/Toast';
import { ClientPicker } from '../../clients/components/ClientPicker';
import { useInboxRefresh } from '../hooks';

/** Narzędzie widoczne tylko w buildzie deweloperskim dla owner/admin (backend i tak sprawdza NODE_ENV i rolę). */
export function canSimulateEvents(access: { isAdmin: boolean }): boolean {
  return import.meta.env.DEV && access.isAdmin;
}

const DEFAULTS: Record<SimulatedEventKind, { severity: ReclassifiableSeverity; title: string; body: string }> = {
  message: {
    severity: 'normal',
    title: 'Prośba o wycenę',
    body: 'Dzień dobry,\n\nczy możemy prosić o wycenę nowej funkcji? Zależy nam na starcie w przyszłym miesiącu.\n\nPozdrawiam',
  },
  outage: {
    severity: 'critical',
    title: 'Strona nie odpowiada',
    body: '2 nieudane sprawdzenia z rzędu (błąd połączenia).',
  },
  agent_finished: {
    severity: 'normal',
    title: 'Agent skończył pracę',
    body: 'Zmiany są gotowe do przejrzenia.',
  },
};

type Errors = Partial<Record<'title' | 'clientId' | 'body', string>>;

/**
 * „Symuluj zdarzenie” (Etap 4: „Użyj symulowanych zdarzeń”): wiadomość od klienta, awaria, ukończenie agenta →
 * `inbox.simulate` (źródło „Symulacja”, nic nie zostaje wysłane). Po zapisie otwiera pozycję w Inboxie.
 */
export function SimulateEventDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const access = useAccess();
  const navigate = useNavigate();
  const refresh = useInboxRefresh();
  const mutation = trpc.inbox.simulate.useMutation();
  const [kind, setKind] = useState<SimulatedEventKind>('message');
  const [severity, setSeverity] = useState<ReclassifiableSeverity>('normal');
  const [clientId, setClientId] = useState<string | null>(null);
  const [title, setTitle] = useState(DEFAULTS.message.title);
  const [body, setBody] = useState(DEFAULTS.message.body);
  const [audience, setAudience] = useState<SimulationAudience>('me');
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setKind('message');
    setSeverity(DEFAULTS.message.severity);
    setClientId(null);
    setTitle(DEFAULTS.message.title);
    setBody(DEFAULTS.message.body);
    setAudience('me');
    setErrors({});
    setFormError(null);
  }, [open]);

  const changeKind = (k: SimulatedEventKind) => {
    setKind(k);
    setSeverity(DEFAULTS[k].severity);
    setTitle(DEFAULTS[k].title);
    setBody(DEFAULTS[k].body);
    setErrors({});
  };

  const submit = async () => {
    setFormError(null);
    const next: Errors = {};
    if (!title.trim()) next.title = 'Wpisz temat';
    if (kind === 'message' && !clientId) next.clientId = 'Wybierz klienta';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      const res = await mutation.mutateAsync({
        kind,
        severity,
        clientId,
        title: title.trim(),
        ...(body.trim() ? { body: body.trim() } : {}),
        audience,
      });
      await refresh();
      onOpenChange(false);
      toast.success('Dodano symulowane zdarzenie', { description: 'Nic nie zostało wysłane.' });
      if (res.inboxItemId) void navigate(`/inbox/${res.inboxItemId}`);
    } catch (err) {
      const issues = validationIssues(err);
      if (issues.length > 0) {
        const mapped: Errors = {};
        for (const i of issues)
          if (i.path === 'title' || i.path === 'clientId' || i.path === 'body') mapped[i.path] = i.message;
        setErrors(mapped);
        if (Object.keys(mapped).length === 0) setFormError(issues[0]?.message ?? 'Sprawdź pola formularza.');
      } else {
        setFormError(errorMessage(err, 'Nie udało się dodać zdarzenia.'));
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Symuluj zdarzenie"
      description="Narzędzie deweloperskie: zdarzenie trafi do Inboxu jak prawdziwe, z oznaczeniem „Symulacja”."
      width="l"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" loading={mutation.isPending} onClick={() => void submit()}>
            Dodaj zdarzenie
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ContextBanner tone="info">{EVENT_SIMULATION_NOTE}.</ContextBanner>
        {formError && <FormMessage>{formError}</FormMessage>}
        <Field label="Rodzaj">
          <Select<SimulatedEventKind>
            value={kind}
            onValueChange={changeKind}
            options={SIMULATED_EVENT_KINDS.map((k) => ({ value: k, label: SIMULATED_EVENT_KIND_LABELS[k] }))}
          />
        </Field>
        <Field label="Ważność">
          <Segmented<ReclassifiableSeverity>
            aria-label="Ważność"
            fullWidth
            value={severity}
            onValueChange={setSeverity}
            options={RECLASSIFIABLE_SEVERITIES.map((s) => ({ value: s, label: SEVERITY_LABELS[s] }))}
          />
        </Field>
        {access.can('clients') && (
          <Field label={kind === 'message' ? 'Klient' : 'Klient (opcjonalnie)'} error={errors.clientId}>
            <ClientPicker
              value={clientId}
              onChange={(id) => {
                setClientId(id);
                if (errors.clientId) setErrors((e) => ({ ...e, clientId: undefined }));
              }}
              allowNone={kind !== 'message'}
              noneLabel="Bez klienta"
            />
          </Field>
        )}
        <Field label="Temat" error={errors.title}>
          <Input value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Treść" error={errors.body}>
          <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <Field label="Odbiorcy">
          <Select<SimulationAudience>
            value={audience}
            onValueChange={setAudience}
            options={SIMULATION_AUDIENCES.map((a) => ({ value: a, label: SIMULATION_AUDIENCE_LABELS[a] }))}
          />
        </Field>
      </div>
    </Dialog>
  );
}
